import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Send } from "lucide-react";
import { API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { EASE } from "@/components/Reveal";

export default function InquiryDrawer({ open, onClose, artwork = null }) {
  const [form, setForm] = useState({ name: "", email: "", message: "", framing: "", country: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open && artwork) {
      setForm((f) => ({
        ...f,
        message: `Hello Richard, I'm interested in "${artwork.title}"${artwork.price_display ? ` (${artwork.price_display})` : ""}. `,
      }));
    }
  }, [open, artwork]);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await API.post("/inquiries", {
        ...form,
        artwork_id: artwork?.id || null,
        artwork_title: artwork?.title || null,
        framing: form.framing || null,
        country: form.country || null,
      });
      toast.success("Your inquiry is on its way", { description: "Richard replies personally, usually within two days." });
      setForm({ name: "", email: "", message: "", framing: "", country: "" });
      onClose();
    } catch (err) {
      toast.error(formatApiError(err, "The inquiry could not be sent."));
    } finally {
      setSending(false);
    }
  };

  const field = "w-full bg-paper border border-ink/15 rounded-md px-4 py-3 text-sm text-ink placeholder:text-subtle focus:outline-none focus:border-terracotta/60 transition-colors";

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-ink/45 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.aside
            data-testid="inquire-drawer"
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ duration: 0.5, ease: EASE }}
            className="fixed top-0 right-0 bottom-0 z-[90] w-full sm:max-w-md bg-linen border-l border-ink/10 overflow-y-auto"
          >
            <div className="p-6 sm:p-8">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-sage">Collector Inquiry</p>
                  <h3 className="font-serif text-2xl sm:text-3xl text-ink mt-2 leading-tight">
                    {artwork ? <>Inquire on <em>{artwork.title}</em></> : "Inquire with the Studio"}
                  </h3>
                </div>
                <button data-testid="inquire-drawer-close" onClick={onClose} aria-label="Close"
                  className="w-9 h-9 rounded-full border border-ink/15 flex items-center justify-center hover:bg-ink hover:text-linen transition-colors">
                  <X size={16} />
                </button>
              </div>

              {artwork && (
                <div className="mt-6 flex gap-4 items-center bg-paper border border-ink/10 rounded-md p-3">
                  <img src={artwork.image} alt={artwork.title} className="w-16 h-16 object-cover rounded-sm border border-ink/10" />
                  <div>
                    <p className="font-serif text-lg leading-tight">{artwork.title}</p>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone2 mt-1">{artwork.dimensions}</p>
                  </div>
                </div>
              )}

              <form onSubmit={submit} className="mt-6 space-y-4">
                <input data-testid="inquire-form-name" required placeholder="Your name" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} />
                <input data-testid="inquire-form-email" required type="email" placeholder="Email address" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} className={field} />
                {artwork?.frame && (
                  <select data-testid="inquire-form-framing" value={form.framing}
                    onChange={(e) => setForm({ ...form, framing: e.target.value })} className={field}>
                    <option value="">Framing preference (optional)</option>
                    <option>Keep current frame — {artwork.frame}</option>
                    <option>Natural white oak floating frame</option>
                    <option>Hand-splined American walnut</option>
                    <option>Unframed, crated for shipping</option>
                  </select>
                )}
                <input data-testid="inquire-form-country" placeholder="Shipping country (optional)" value={form.country}
                  onChange={(e) => setForm({ ...form, country: e.target.value })} className={field} />
                <textarea data-testid="inquire-form-message" required rows={5} placeholder="Your message to the studio"
                  value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={field} />
                <button data-testid="inquire-form-submit" disabled={sending} type="submit"
                  className="w-full inline-flex items-center justify-center gap-2 bg-ink text-linen rounded-full py-3.5 text-xs font-mono uppercase tracking-[0.2em] hover:bg-terracotta transition-colors duration-300 disabled:opacity-60">
                  <Send size={14} /> {sending ? "Sending…" : "Send Inquiry"}
                </button>
                <p className="text-[11px] text-stone2 leading-relaxed text-center">
                  Every inquiry is answered personally by Richard. No obligation — provenance and framing advice included.
                </p>
              </form>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
